import { usePathname } from 'next/navigation';
import { type FC, useCallback, useEffect } from 'react';
import { ImageComponent } from '@/components/Image';
import { useNavigation } from '@/hooks/navigation';
import { useComponentVisible } from '@/hooks/visibility';
import { useMissionLevels } from '@/modules/promotions/templates/HeadlinePromoTemplate/components/MissionLevels/hooks/useMissionLevels';
import type { IHeadlinePromoPageConfig } from '@/modules/promotions/templates/HeadlinePromoTemplate/helpers';
import { useGetCommonStore } from '@/store';
import { ScRelativeContainer } from '@/styles/common';
import { HeadlinePromoSection } from '../..';
import {
  ScHeadlinePromoButton,
  ScHeadlinePromoDesktopPopup,
} from '../../styled';

interface IHeadlinePromoDesktopWidgetProps {
  config: IHeadlinePromoPageConfig;
}

export const HeadlinePromoDesktopWidget: FC<
  IHeadlinePromoDesktopWidgetProps
> = ({ config }) => {
  const { navigate } = useNavigation();

  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const pathname = usePathname();

  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const { levels, currentLevelIndex } = useMissionLevels(
    config.levelsConfig,
    config.groupPrefix,
    config.useFullNameAsGroupId
  );
  const currentLevel = levels[currentLevelIndex];

  const handleToggle = useCallback(
    () => setIsComponentVisible((prev) => !prev),
    [setIsComponentVisible]
  );

  const handleClose = useCallback(
    () => setIsComponentVisible(false),
    [setIsComponentVisible]
  );

  const handleNavigate = useCallback(
    () => navigate(config.route),
    [navigate, config.route]
  );

  useEffect(() => {
    setIsComponentVisible(false);
  }, [pathname, setIsComponentVisible]);

  if (isAuthenticated && !currentLevel) return null;
  if (!(isAuthenticated || config.levelsConfig.length)) return null;

  const issued = levels.some((level) => level.state === 'issued');
  const oversizedArt = !!config.levelsConfig[currentLevelIndex]?.artOverflows;

  return (
    <ScRelativeContainer>
      <ScHeadlinePromoButton
        $issued={issued}
        $oversizedArt={oversizedArt}
        onClick={isAuthenticated ? handleToggle : handleNavigate}
        ref={openerRef}
      >
        <ImageComponent
          alt=""
          height={40}
          src={
            isAuthenticated ? currentLevel.image : config.levelsConfig[0].image
          }
          width={40}
        />
      </ScHeadlinePromoButton>
      {isAuthenticated && isComponentVisible && (
        <ScHeadlinePromoDesktopPopup
          onClick={(event) => event.stopPropagation()}
          ref={ref}
        >
          <HeadlinePromoSection config={config} onLinkClick={handleClose} />
        </ScHeadlinePromoDesktopPopup>
      )}
    </ScRelativeContainer>
  );
};
